import { BitkitEmptyState, BitkitNativeSelect, IconFilter } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import capitalize from 'lodash/capitalize';
import { ChangeEvent, Fragment, useCallback, useMemo } from 'react';

import { BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';
import InvocationListItemMobile from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListItemMobile';
import {
  SortByQueryParamValues,
  SortDirectionQueryParamValues,
} from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/enums/invocationListTable';

const selectValueFromSortByAndSortDirection = (
  sortBy: SortByQueryParamValues | null,
  sortDirection: SortDirectionQueryParamValues | null,
) => {
  return `${sortBy || ''}-${sortDirection || ''}`;
};

const sortByAndSortDirectionFromSelectValue = (selectValue: string) => {
  const [part1, part2] = selectValue.split('-');

  return {
    sortBy: (part1 || null) as SortByQueryParamValues | null,
    sortDirection: (part2 || null) as SortDirectionQueryParamValues | null,
  };
};

const sortButtonLabel = (
  property: SortByQueryParamValues | null,
  direction: SortDirectionQueryParamValues.ascending | SortDirectionQueryParamValues.descending | null,
  compact: boolean,
) => {
  if (property === null || direction === null) {
    return 'Not sorted';
  }

  const sortByLabels: Record<SortByQueryParamValues, string> = {
    started_at: 'started at',
    duration: 'duration',
  };

  const sortDirectionLabels: Record<
    SortByQueryParamValues,
    Record<SortDirectionQueryParamValues.ascending | SortDirectionQueryParamValues.descending, string>
  > = {
    started_at: {
      ascending: 'lowest',
      descending: 'highest',
    },
    duration: {
      ascending: 'fastest',
      descending: 'slowest',
    },
  };

  return compact
    ? `${capitalize(sortByLabels[property])} (${sortDirectionLabels[property][direction]})`
    : `Sort by ${sortByLabels[property]} (${sortDirectionLabels[property][direction]})`;
};

type InvocationListMobileProps = {
  invocations: BuildToolInvocationWithDetails[];
  sortBy: SortByQueryParamValues | null;
  sortDirection: SortDirectionQueryParamValues | null;
  onStartedAtSortClick: (sortDirection: SortDirectionQueryParamValues) => void;
  onDurationSortClick: (sortDirection: SortDirectionQueryParamValues) => void;
  onSortClear: () => void;
};

const InvocationListMobile = ({
  invocations,
  sortBy,
  sortDirection,
  onStartedAtSortClick,
  onDurationSortClick,
  onSortClear,
}: InvocationListMobileProps) => {
  const sortByWithSortDirection = useMemo(() => {
    return selectValueFromSortByAndSortDirection(sortBy, sortDirection);
  }, [sortBy, sortDirection]);

  const onSortByWithSortDirectionSelected = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const { sortBy: selectedSortBy, sortDirection: selectedSortDirection } = sortByAndSortDirectionFromSelectValue(
        event.target.value,
      );

      switch (selectedSortBy as SortByQueryParamValues | null) {
        case SortByQueryParamValues.startedAt:
          onStartedAtSortClick(selectedSortDirection as SortDirectionQueryParamValues);
          break;
        case SortByQueryParamValues.duration:
          onDurationSortClick(selectedSortDirection as SortDirectionQueryParamValues);
          break;
        case null:
          onSortClear();
          break;
      }
    },
    [onStartedAtSortClick, onDurationSortClick, onSortClear],
  );

  return (
    <>
      <BitkitNativeSelect
        value={sortByWithSortDirection}
        onChange={onSortByWithSortDirectionSelected}
        placeholder="Sort"
        label="Sort"
        labelHidden
        optional
        marginBlockEnd="16"
      >
        <option value={selectValueFromSortByAndSortDirection(null, null)}>{sortButtonLabel(null, null, true)}</option>
        {[SortByQueryParamValues.startedAt, SortByQueryParamValues.duration].map((aSortBy) => {
          return (
            <Fragment key={aSortBy}>
              <option value={selectValueFromSortByAndSortDirection(aSortBy, SortDirectionQueryParamValues.ascending)}>
                {sortButtonLabel(aSortBy, SortDirectionQueryParamValues.ascending, true)}
              </option>
              <option value={selectValueFromSortByAndSortDirection(aSortBy, SortDirectionQueryParamValues.descending)}>
                {sortButtonLabel(aSortBy, SortDirectionQueryParamValues.descending, true)}
              </option>
            </Fragment>
          );
        })}
      </BitkitNativeSelect>
      <Box display="flex" flexDirection="column" gap="1.5rem">
        {invocations.length === 0 && (
          <BitkitEmptyState
            icon={IconFilter}
            headingText="No invocations match your criteria"
            bodyText="Modify your filters to get results."
          />
        )}
        {invocations.map((invocation) => (
          <InvocationListItemMobile key={invocation.invocationId} invocation={invocation} />
        ))}
      </Box>
    </>
  );
};

export default InvocationListMobile;
